import * as R from 'ramda'
import {
  ElevatorQuestion,
  Elevator,
  EscalatorQuestion,
  Escalator,
  Offer,
  ClientFrontlineConfig,
  BuildingQuestion,
  Building,
  AssetStatusDetailsList,
  Contact,
  Partner,
  ContractDetails,
  Customer,
  Option as ComboBoxOption,
  BadgeVariant,
  EquipmentGroupStId,
  EquipmentStId,
  BuildingStId,
  EquipmentType
} from 'types/types'
import { getElevatorValidation, getEscalatorValidation, getBuildingValidation } from '../nemo/assessment/validations'
import {
  getFrontlineEnabledEscalatorTypes,
  getFrontlineEnabledTechnicalPlatforms,
  getEarliestDate,
  capitalizeIfEnglish
} from './utils'
import moment from 'moment'
import { formatPrice } from '../nemo/pricing/utils'
import { t } from 'i18next'
import { isMarketSegmentMissing, isFundingSectorMissing } from '../nemo/offer'

type ValidationResult =
  | { type: 'valid' }
  | { type: 'notAnswered'; reason: string }
  | { type: 'invalid'; reason: string }

interface ContactsWithPrimary {
  contacts: Contact[]
  primaryContact: Contact | undefined
}

interface ValidationError {
  id: string
  message: string
}

export const getOpportunityValidationErrors = (offer: Offer): ValidationError[] => {
  if (!offer) {
    return []
  }

  const errors: ValidationError[] = []

  if (offer.opportunity && isMarketSegmentMissing(offer.opportunity)) {
    errors.push({
      id: 'market-segment-error',
      message: `${t('salesforce.opportunity.missingMarketSegment')}, ${t('salesforce.opportunity.edit')}`
    })
  }

  if (offer.opportunity && isFundingSectorMissing(offer.opportunity)) {
    errors.push({
      id: 'funding-sector-error',
      message: `${t('salesforce.opportunity.missingFundingSector')}, ${t('salesforce.opportunity.edit')}`
    })
  }

  if (offer.customer && !isAccountNumberPresent(offer.customer)) {
    errors.push({ id: 'customer-account-number-error', message: t('prepare.accountNumberWarning') })
  }

  if (!offer.contacts || offer.contacts.length === 0) {
    errors.push({ id: 'contact-missing-error', message: t('prepare.contactWarning') })
  } else if (offer.contacts.every(contact => !contact.role)) {
    errors.push({ id: 'contact-missing-role-error', message: t('prepare.contactRoleWarning') })
  }

  return errors
}

export const equipmentCategory = {
  ELEVATOR: 'elevator',
  ESCALATOR: 'escalator'
}

export const accountFields = [
  { label: 'Account number', value: (customer: Customer | undefined) => customer?.accountNumber },
  { label: 'Name', value: (customer: Customer | undefined) => customer?.name },
  { label: 'Address', value: (customer: Customer | undefined) => customer?.address },
  { label: 'Zip Code', value: (customer: Customer | undefined) => customer?.zipCode },
  { label: 'City', value: (customer: Customer | undefined) => customer?.city },
  { label: 'Province', value: (customer: Customer | undefined) => customer?.state }
]

export const partnerFields = [
  { label: 'Account number', value: (partner: Partner | undefined) => partner?.accountNumber },
  { label: 'Province', value: (partner: Partner | undefined) => partner?.state },
  { label: 'Name', value: (partner: Partner | undefined) => partner?.name },
  { label: 'Address', value: (partner: Partner | undefined) => partner?.address },
  { label: 'Zip Code', value: (partner: Partner | undefined) => partner?.zipCode },
  { label: 'City', value: (partner: Partner | undefined) => partner?.city }
]

// Utility to extract display fields from a Contact
export function getContactDisplayFields(contact?: Contact) {
  if (!contact) {
    return {
      name: '-',
      address: '-',
      phone: '-',
      email: '-',
      mobile: '-',
      role: '-',
      salutation: '-',
      customerType: '-',
      primaryContact: undefined
    }
  }

  const {
    country = '',
    city = '',
    phone = '',
    email = '',
    mobile = '',
    role = '',
    salutation = '',
    customerType = '',
    primaryContact,
    lastName = '',
    firstName = ''
  } = contact

  const address = city ? `${city}, ${country}` : country
  const name = lastName && firstName ? `${lastName}, ${firstName}` : lastName || firstName

  return {
    name,
    address,
    phone,
    email: email || '-',
    mobile: mobile || '-',
    role,
    salutation,
    customerType,
    primaryContact
  }
}

export function mergeContactRoles(contacts: Contact[]): Contact[] {
  // Group contacts by id
  const grouped = R.groupBy((contact: Contact) => String(contact.id), contacts)
  return Object.values(grouped).map(group => {
    const base = group[0]
    // Merge all roles for this contact
    const mergedRole = group
      .map(contact => contact.role)
      .filter(Boolean)
      .join(', ')
    // If any contact in the group is primary, set as primary
    const primaryContact = group.some(contact => contact.primaryContact)
    return {
      ...base,
      role: mergedRole,
      primaryContact
    }
  })
}

export function preparePrimaryContact(contacts: ReadonlyArray<Contact>): ContactsWithPrimary {
  let primaryContact = contacts.find(contact => contact.primaryContact)
  let contactsArr = [...contacts]
  if (contacts.length && !primaryContact) {
    // Promote the first contact to primary (immutably)
    primaryContact = { ...contactsArr[0], primaryContact: true }
    contactsArr = [primaryContact, ...contactsArr.slice(1)]
  }
  return {
    contacts: contactsArr,
    primaryContact
  }
}

export const isAllFieldsEmpty = (partner: Partner | null | undefined) => {
  if (!partner) {
    return true
  }
  // Check all fields except 'Custom details' (which is always '-')
  return [partner.accountNumber, partner.city, partner.name, partner.state, partner.address, partner.zipCode].every(
    val => !val || val === '' || val === undefined || val === null
  )
}

export const getContractDetails = (offer: Offer | undefined): ContractDetails => {
  let contractDetails: ContractDetails = {
    activeContracts: '',
    startDate: '',
    endDate: '',
    contractValue: ''
  }
  if (!offer) {
    return contractDetails
  }
  const contracts = offer.opportunity?.contracts
  if (contracts && contracts.length > 0) {
    const totalContractAmount = contracts.reduce((sum, contract) => sum + contract.annualContractValue, 0)
    const activatedContracts = contracts.length.toString()
    const latestContractStartDate = getEarliestDate(contracts, 'start')
    const latestContractEndDate = getEarliestDate(contracts, 'end')
    const startDateFormatted = latestContractStartDate ? moment(latestContractStartDate).format('DD/MM/YYYY') : null
    const endDateFormatted = latestContractEndDate ? moment(latestContractEndDate).format('DD/MM/YYYY') : null
    const finalValue = formatPrice(totalContractAmount, 'en', contracts[0].currency) // TODO: Update this to use the actual language from the user settings
    contractDetails = {
      activeContracts: activatedContracts,
      startDate: startDateFormatted ? startDateFormatted : '',
      endDate: endDateFormatted ? endDateFormatted : '',
      contractValue: finalValue ?? ''
    }
  }

  return contractDetails
}

// Helper to check if question validation failed
const hasValidationError = (result: ValidationResult, isMandatory: boolean): boolean =>
  result.type === 'invalid' || (result.type === 'notAnswered' && isMandatory)

// Helper to create error object
const createAssetValidationError = (params: {
  category: 'building' | 'elevator' | 'escalator'
  explanation: string
  id: string
  parentId: string
  questionKey: string
}) => ({
  category: params.category,
  explanation: params.explanation,
  id: params.id,
  module: 'details' as const,
  parentId: params.parentId,
  questionKey: params.questionKey
})

// Helper to process equipment validation
const processEquipmentValidation = <T extends Elevator | Escalator>(
  equipment: T,
  questions: Array<ElevatorQuestion | EscalatorQuestion>,
  validationFn: (question: any, equipment: T, config: string[]) => ValidationResult,
  config: string[],
  equipmentData: AssetStatusDetailsList,
  category: 'elevator' | 'escalator',
  buildingSalesToolId: string
) => {
  let hasError = false

  questions.forEach(question => {
    const questionStatus = validationFn(question, equipment, config)

    if (hasValidationError(questionStatus, question.mandatory)) {
      equipmentData[category].errorList.push(
        createAssetValidationError({
          category,
          explanation: questionStatus.type !== 'valid' ? questionStatus.reason : '',
          id: equipment.salesToolId,
          parentId: buildingSalesToolId,
          questionKey: question.key
        })
      )
      hasError = true
    }
  })

  equipmentData[category].count += 1
  if (hasError) {
    equipmentData[category].errorCount += 1
  }
}

// Helper to process building validation
const processBuildingValidation = (
  building: Building,
  buildingQuestions: BuildingQuestion[],
  equipmentData: AssetStatusDetailsList
) => {
  let hasError = false

  buildingQuestions.forEach(question => {
    const questionStatus = isBuildingQuestionValid(question, building)

    if (hasValidationError(questionStatus, question.mandatory)) {
      equipmentData.location.errorList.push(
        createAssetValidationError({
          category: 'building',
          explanation: questionStatus.type !== 'valid' ? questionStatus.reason : '',
          id: building.salesToolId,
          parentId: '',
          questionKey: question.key
        })
      )
      hasError = true
    }
  })

  if (building.details.equipment.length === 0) {
    equipmentData.location.errorList.push(
      createAssetValidationError({
        category: 'building',
        explanation: capitalizeIfEnglish(t('xlAssessmentWizard.equipment.noEquipment')),
        id: building.salesToolId,
        parentId: '',
        questionKey: 'equipment'
      })
    )
    hasError = true
  }

  equipmentData.location.count += 1
  if (hasError) {
    equipmentData.location.errorCount += 1
  }
}

export const getAssetStatusDetails = (offer: Offer | undefined, frontlineConfig: ClientFrontlineConfig | undefined) => {
  const equipmentData: AssetStatusDetailsList = {
    location: {
      label: t('prepare.refresh.location'),
      count: 0,
      errorCount: 0,
      errorList: []
    },
    elevator: {
      label: t('review.elevator'),
      count: 0,
      errorCount: 0,
      errorList: []
    },
    escalator: {
      label: t('review.escalator'),
      count: 0,
      errorCount: 0,
      errorList: []
    }
  }

  if (!offer?.buildings?.length || !frontlineConfig) {
    console.warn('No equipment data available in the offer')
    return equipmentData
  }

  const frontlineEnabledTechnicalPlatforms = getFrontlineEnabledTechnicalPlatforms(frontlineConfig)
  const frontlineEnabledEscalatorTypes = getFrontlineEnabledEscalatorTypes(frontlineConfig)

  offer.buildings.forEach(building => {
    // Process equipment (elevators and escalators)
    building.details.equipment.forEach(equipment => {
      if (equipment.equipmentCategory === 'elevator') {
        processEquipmentValidation(
          equipment,
          frontlineConfig.elevatorQuestions,
          isElevatorQuestionValid,
          frontlineEnabledTechnicalPlatforms,
          equipmentData,
          'elevator',
          building.details.salesToolId
        )
      } else if (equipment.equipmentCategory === 'escalator') {
        processEquipmentValidation(
          equipment,
          frontlineConfig.escalatorQuestions,
          isEscalatorQuestionValid,
          frontlineEnabledEscalatorTypes,
          equipmentData,
          'escalator',
          building.details.salesToolId
        )
      }
    })

    // Process building/location validation
    processBuildingValidation(building, frontlineConfig.buildingQuestions, equipmentData)
  })

  return equipmentData
}

export const isBuildingQuestionValid = (question: BuildingQuestion, building: Building): ValidationResult => {
  const { key } = question
  const answer = building.details[key]

  if (typeof answer === 'undefined' || answer === null || answer === '') {
    return { type: 'notAnswered', reason: t('xlAssessmentWizard.validation.answerRequired') }
  }

  const validator = getBuildingValidation(question)
  if (!validator) {
    return { type: 'invalid', reason: t('xlAssessmentWizard.validation.answerRequired') }
  }

  const isValid = validator.validate(answer)
  if (!isValid) {
    const reason = validator.getErrorReason?.(answer, question) ?? t('xlAssessmentWizard.validation.invalidValue')
    return { type: 'invalid', reason }
  }

  return { type: 'valid' }
}

export const isElevatorQuestionValid = (
  question: ElevatorQuestion,
  elevator: Elevator,
  frontlineEnabledTechnicalPlatforms: string[]
): ValidationResult => {
  const { key } = question
  const answer = elevator[key]
  const { isAPFFromCRM } = elevator

  if (typeof answer === 'undefined') {
    return { type: 'notAnswered', reason: t('xlAssessmentWizard.validation.answerRequired') }
  }

  const validator = getElevatorValidation(question, frontlineEnabledTechnicalPlatforms, isAPFFromCRM)
  const isValid = validator.validate(answer)

  if (!isValid) {
    const reason = validator.getErrorReason?.(answer, question) ?? t('xlAssessmentWizard.validation.invalidValue')
    return { type: 'invalid', reason }
  }

  return { type: 'valid' }
}

export const isEscalatorQuestionValid = (
  question: EscalatorQuestion,
  escalator: Escalator,
  frontlineEnabledEscalatorTypes: string[]
): ValidationResult => {
  const { key } = question
  const answer = escalator[key]

  if (typeof answer === 'undefined') {
    return { type: 'notAnswered', reason: t('xlAssessmentWizard.validation.answerRequired') }
  }

  const validator = getEscalatorValidation(question, escalator.equipmentType, frontlineEnabledEscalatorTypes)
  const isValid = validator.validate(answer)

  if (!isValid) {
    const reason = validator.getErrorReason?.(answer, question) ?? t('xlAssessmentWizard.validation.invalidValue')
    return { type: 'invalid', reason }
  }

  return { type: 'valid' }
}

// Helper to validate all equipment questions and return errors
export const validateEquipmentQuestions = (
  equipmentType: 'elevator' | 'escalator',
  equipment: Elevator | Escalator,
  frontlineConfig: ClientFrontlineConfig
): Record<string, string> => {
  const errors: Record<string, string> = {}

  if (equipmentType === 'elevator') {
    const frontlineEnabledTechnicalPlatforms = getFrontlineEnabledTechnicalPlatforms(frontlineConfig)
    const elevatorQuestions: ElevatorQuestion[] = [
      ...frontlineConfig.elevatorQuestions,
      {
        key: 'startsPerYear',
        mandatory: true,
        readonly: false,
        subject: 'elevator',
        type: 'select'
      } as ElevatorQuestion
    ]

    elevatorQuestions.forEach(question => {
      const questionStatus = isElevatorQuestionValid(
        question,
        equipment as Elevator,
        frontlineEnabledTechnicalPlatforms
      )

      if (questionStatus.type === 'invalid' || (questionStatus.type === 'notAnswered' && question.mandatory)) {
        errors[question.key] = questionStatus.reason
      }
    })
  } else if (equipmentType === 'escalator') {
    const frontlineEnabledEscalatorTypes = getFrontlineEnabledEscalatorTypes(frontlineConfig)
    const escalatorQuestions: EscalatorQuestion[] = frontlineConfig.escalatorQuestions

    escalatorQuestions.forEach(question => {
      const questionStatus = isEscalatorQuestionValid(question, equipment as Escalator, frontlineEnabledEscalatorTypes)

      if (questionStatus.type === 'invalid' || (questionStatus.type === 'notAnswered' && question.mandatory)) {
        errors[question.key] = questionStatus.reason
      }
    })
  }

  return errors
}

// Helper to validate all building/location questions and return errors
export const validateBuildingQuestions = (
  building: Building,
  frontlineConfig: ClientFrontlineConfig
): Record<string, string> => {
  const errors: Record<string, string> = {}
  const buildingQuestions: BuildingQuestion[] = frontlineConfig.buildingQuestions

  buildingQuestions.forEach(question => {
    const questionStatus = isBuildingQuestionValid(question, building)

    if (questionStatus.type === 'invalid' || (questionStatus.type === 'notAnswered' && question.mandatory)) {
      errors[question.key] = questionStatus.reason
    }
  })

  if (building.details.equipment.length === 0) {
    errors.equipment = capitalizeIfEnglish(t('xlAssessmentWizard.equipment.noEquipment'))
  }
  return errors
}

export const joinSfNameFields = (model: any) => {
  const { name, name2, name3, name4 } = model
  return [name, name2, name3, name4].filter(Boolean).join(' ')
}

// Helper to merge accountFields with additional fields
export const getAccountFieldConfigs = (translation: (key: string) => string) => [
  ...accountFields,
  { label: capitalizeIfEnglish(translation('review.poBox')), value: (customer: Customer) => customer?.poBox },
  {
    label: capitalizeIfEnglish(translation('review.poBoxPostalCode')),
    value: (customer: Customer) => customer?.poBoxPostalCode
  },
  { label: capitalizeIfEnglish(translation('review.poBoxCity')), value: (customer: Customer) => customer?.poBoxCity },
  { label: capitalizeIfEnglish(translation('review.vat')), value: (customer: Customer) => customer?.vat },
  { label: capitalizeIfEnglish(translation('review.taxNumber')), value: (customer: Customer) => customer?.taxNumber },
  { label: capitalizeIfEnglish(translation('review.taxNumber2')), value: (customer: Customer) => customer?.taxNumber2 },
  { label: capitalizeIfEnglish(translation('review.taxNumber3')), value: (customer: Customer) => customer?.taxNumber3 }
]

// Field mapping for ContractParty keys
export const contractPartyFieldMapping = {
  'Account number': 'accountNumber',
  Name: 'name',
  Address: 'address',
  'Zip Code': 'zipCode',
  City: 'city',
  Province: 'state',
  Country: 'country',
  'PO box no': 'poBox',
  'PO box postal code': 'poBoxPostalCode',
  'PO box city': 'poBoxCity',
  'BUSINESS ID / VAT REG': 'vat',
  'Tax number 1': 'taxNumber',
  'Tax number 2': 'taxNumber2',
  'Tax number 3': 'taxNumber3',
  'Custom details': 'freeText'
} as const

export const getContactName = (contact: Contact) => {
  if (!contact) {
    return ''
  }
  const { salutation, lastName, firstName } = contact
  const safeSalutation = salutation ? salutation : ''
  const safeLastName = lastName ? lastName : ''
  const safeFirstName = firstName ? firstName : ''
  return `${safeSalutation} ${safeLastName}, ${safeFirstName}`
}

export const ContactRoleOptions: ComboBoxOption[] = [
  { key: 'Decision Maker', value: 'contact.decisionMakerRole' },
  { key: 'Influencer', value: 'contact.influencerRole' },
  { key: 'Estimator', value: 'contact.estimatorRole' },
  { key: 'End User', value: 'contact.endUserRole' },
  { key: 'Project Manager', value: 'contact.projectManagerRole' },
  { key: 'Other', value: 'contact.otherRole' }
]

export const getEquipmentDetailsByType = (offerData: Offer, searchQuery: string) => {
  const buildings = offerData?.buildings || []
  const elevators: Elevator[] = []
  const escalators: Escalator[] = []

  buildings.forEach(building => {
    building.details.equipment?.forEach(equipment => {
      if (equipment.equipmentCategory?.toLocaleLowerCase() === 'elevator') {
        elevators.push(equipment as Elevator)
      } else if (equipment.equipmentCategory?.toLocaleLowerCase() === 'escalator') {
        escalators.push(equipment as Escalator)
      }
    })
  })

  const equipmentDetails = {
    buildings,
    elevators,
    escalators
  }

  // Filter by search query if provided
  if (searchQuery && searchQuery.trim() !== '') {
    const filtered = filterEquipmentBySearch(equipmentDetails, searchQuery)
    return {
      building: filtered.buildings,
      elevator: filtered.elevators,
      escalator: filtered.escalators
    }
  }

  return {
    building: buildings,
    elevator: elevators,
    escalator: escalators
  }
}

const filterEquipmentBySearch = (
  equipmentDetails: { buildings: Building[]; elevators: Elevator[]; escalators: Escalator[] },
  searchText: string
) => {
  if (!searchText || searchText.trim() === '') {
    return equipmentDetails
  }
  const lowerSearchText = searchText.toLowerCase()

  const filteredBuildings = equipmentDetails.buildings.filter(building => {
    const buildingFields = Object.values(building.details).map(value => (value ? String(value).toLowerCase() : ''))
    return buildingFields.some(field => field.includes(lowerSearchText))
  })

  const filteredElevators = equipmentDetails.elevators.filter(elevator => {
    const elevatorFields = Object.values(elevator).map(value => (value ? String(value).toLowerCase() : ''))
    return elevatorFields.some(field => field.includes(lowerSearchText))
  })

  const filteredEscalators = equipmentDetails.escalators.filter(escalator => {
    const escalatorFields = Object.values(escalator).map(value => (value ? String(value).toLowerCase() : ''))
    return escalatorFields.some(field => field.includes(lowerSearchText))
  })

  return {
    buildings: filteredBuildings,
    elevators: filteredElevators,
    escalators: filteredEscalators
  }
}

export const getAssetErrorStatusDetails = (
  assetId: BuildingStId | EquipmentStId | EquipmentGroupStId,
  assetStatusDetails: AssetStatusDetailsList,
  assetType: 'location' | 'escalator' | 'elevator'
): BadgeVariant => {
  if (assetStatusDetails[assetType].errorCount === 0) {
    return 'success'
  }
  const hasError = assetStatusDetails[assetType].errorList?.some(error => error.id === assetId)
  return hasError ? 'error' : 'success'
}

export function getEquipment(data: Elevator | Escalator) {
  switch (data.equipmentCategory) {
    case 'elevator':
      return data.id
    case 'escalator':
      return data.id
    default:
      return null
  }
}

export function isDataGridBuilding(row: Building): row is Building {
  return row.hasOwnProperty('details')
}

function getIdData(data: Building | Elevator | Escalator): string | null {
  return isDataGridBuilding(data as Building)
    ? (data as Building).details.id
    : getEquipment(data as Elevator | Escalator)
}

export function isAssetCreatedInSalesforce(data: Building | Elevator | Escalator): boolean {
  const idData = getIdData(data)
  return idData != null && idData !== 'sales_tool_created'
}

export function isAPFFieldEmpty(data: Elevator): boolean {
  return data!.apfComponent === null
}

export const getEquipmentQuestionByType = (equipmentType: EquipmentType, frontLineConfig: ClientFrontlineConfig) => {
  switch (equipmentType) {
    case 'elevator':
      return frontLineConfig.elevatorQuestions
    case 'escalator':
      return frontLineConfig.escalatorQuestions
    default:
      return []
  }
}

const isAccountNumberPresent = (customer: Customer | undefined): boolean => !!customer?.accountNumber

export const isPrepareInprogress = (offer: Offer, frontlineConfig: ClientFrontlineConfig) => {
  const { contacts, buildings, equipmentGroups, opportunity, customer } = offer
  const assetStatusDetails = getAssetStatusDetails(offer, frontlineConfig)
  const {
    location: { count: locationCount, errorCount: locationErrorCount },
    elevator: { count: elevatorCount, errorCount: elevatorErrorCount },
    escalator: { count: escalatorCount, errorCount: escalatorErrorCount }
  } = assetStatusDetails

  const hasErrors =
    locationCount === 0 ||
    (locationCount > 0 && locationErrorCount > 0) ||
    (elevatorCount === 0 && escalatorCount === 0) ||
    (elevatorCount > 0 && elevatorErrorCount > 0) ||
    (escalatorCount > 0 && escalatorErrorCount > 0) ||
    !contacts ||
    contacts.length === 0 ||
    contacts.every(contact => !contact.role) ||
    !buildings ||
    buildings.length === 0 ||
    R.isEmpty(equipmentGroups) ||
    isMarketSegmentMissing(opportunity) ||
    isFundingSectorMissing(opportunity) ||
    !isAccountNumberPresent(customer)

  return hasErrors
}
